import { getNextSetupState } from "./state.js"
import { SetupContext, SetupOrchestratorDependencies, SetupState } from "./flow/types.js"

const stateDescriptions: Record<SetupState, string> = {
  INIT: "Initializing setup session",
  SELECT_DESIGN_SYSTEM: "Selecting design system",
  SELECT_SCAN_SOURCE_PATH: "Selecting source folder for scan",
  SELECT_SCAN_PACKAGES: "Selecting discovered packages for scan",
  RUN_SCAN: "Running repository scan",
  REVIEW_SCAN_OUTPUT: "Reviewing generated scan output",
  UPLOAD_SCAN_RESULTS: "Uploading analysis snapshots",
  SELECT_TEMPLATE_DESTINATION: "Selecting container destination",
  PREPARE_TEMPLATE: "Preparing the container",
  CONFIGURE_DEPENDENCIES: "Selecting design system dependency packages",
  CONFIGURE_PRIVATE_REGISTRY: "Configuring package access",
  VALIDATE_DEPENDENCY_ACCESS: "Validating dependency access",
  UPLOAD_CONTAINER: "Uploading container",
  CREATE_SHARED_CONTEXT: "Creating shared context",
  FINALIZE_AND_REDIRECT: "Finalizing setup and preparing prototype redirect",
  DONE: "Setup complete",
}

export function describeSetupState(state: SetupState): string {
  return stateDescriptions[state]
}

export class SetupOrchestrator {
  constructor(private readonly deps: SetupOrchestratorDependencies) {}

  public async run(initialState: SetupState, initialContext: SetupContext = {}): Promise<SetupContext> {
    let state = initialState
    let context = { ...initialContext }
    await this.transitionTo(state, context)

    while (state !== "DONE") {
      context = await this.runStep(state, context)
      state = getNextSetupState(state)
      await this.transitionTo(state, context)
    }

    return context
  }

  private async transitionTo(state: SetupState, context: SetupContext): Promise<void> {
    await this.deps.onStateTransition?.(state, context)
  }

  private async runStep(state: SetupState, context: SetupContext): Promise<SetupContext> {
    if (state === "DONE") return context
    const handler = this.deps.handlers?.[state]
    if (handler) {
      const nextContext = await handler(context)
      return nextContext ? { ...context, ...nextContext } : context
    }

    const description = describeSetupState(state)
    this.deps.log(`${description} (placeholder)`)
    return context
  }
}
